import { useState } from "react";
import { Badge, EditRowActions, Input, Td, Text, Tr } from "@/components/ui";
import { formatFecha } from "@/shared/format/fecha";
import type { ClaveSatAdmin } from "../services/claves-sat-admin-service";
import { useEditarClaveSat } from "../hooks/use-clave-sat-mutations";

/**
 * Fila con toggle entre lectura y edición inline. Solo `descripcion` es editable · la clave (8 dígitos SAT) es
 * inmutable porque es la PK, y el resto de las columnas las trae el catálogo del SAT (la siguiente
 * actualización también pisa la descripción con la oficial).
 */
export function ClaveSatRow({ clave }: { clave: ClaveSatAdmin }) {
  const [editando, setEditando] = useState(false);
  const [descripcion, setDescripcion] = useState(clave.descripcion);

  const guardar = useEditarClaveSat({
    onEditado: () => setEditando(false),
  });

  const cancelar = () => {
    setDescripcion(clave.descripcion);
    setEditando(false);
  };

  const vigencia = clave.fechaInicioVigencia
    ? `${formatFecha(clave.fechaInicioVigencia)} – ${clave.fechaFinVigencia ? formatFecha(clave.fechaFinVigencia) : "sin fin"}`
    : "—";

  return (
    <Tr className={editando ? "bg-muted/50" : undefined}>
      <Td>
        <Text size="xs" ff="mono">
          {clave.clave}
        </Text>
      </Td>
      <Td>
        {editando ? (
          <Input size="xs" value={descripcion} onChange={(e) => setDescripcion(e.currentTarget.value)} maxLength={400} autoFocus />
        ) : (
          <Text size="sm">{clave.descripcion}</Text>
        )}
      </Td>
      <Td>
        <Text size="xs">{clave.incluirIvaTrasladado ?? "—"}</Text>
      </Td>
      <Td>
        <Text size="xs">{clave.incluirIepsTrasladado ?? "—"}</Text>
      </Td>
      <Td className="whitespace-nowrap">
        <Text size="xs">{vigencia}</Text>
      </Td>
      <Td>
        {clave.estimuloFranjaFronteriza ? (
          <Badge color="info" variant="light">
            Sí
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Td>
      <Td>
        <Text size="xs" c="dimmed" truncate title={clave.palabrasSimilares ?? undefined} className="max-w-[220px]">
          {clave.palabrasSimilares ?? "—"}
        </Text>
      </Td>
      <Td>
        {clave.utilizable ? (
          <Badge color="exito" variant="light">
            Vigente
          </Badge>
        ) : clave.vigente ? (
          <Badge color="advertencia" variant="light">
            Fuera de vigencia
          </Badge>
        ) : (
          <Badge color="slate" variant="light">
            Descontinuada
          </Badge>
        )}
      </Td>
      <Td align="right">
        {editando ? (
          <EditRowActions
            mode="edit"
            saving={guardar.isPending}
            onCancel={cancelar}
            onSave={() => guardar.mutate({ clave: clave.clave, descripcion: descripcion.trim() })}
          />
        ) : (
          <EditRowActions mode="view" onEdit={() => setEditando(true)} />
        )}
      </Td>
    </Tr>
  );
}
